import type { ChangeEvent } from "react";
import type { AudioPlayerProps } from "../../types/audio-player";

import { AlertCircle } from "lucide-react";
import { AudioPlayerMenu } from "./AudioPlayerMenu";
import { AudioPlayerPlayButton } from "./AudioPlayerPlayButton";
import { AudioPlayerProgress } from "./AudioPlayerProgress";
import { AudioPlayerRemainingTime } from "./AudioPlayerRemainingTime";
import { AudioPlayerSkipButton } from "./AudioPlayerSkipButton";

function AudioPlayer({
  ariaLabel = "Audio player",
  className,
  currentTimeSeconds,
  disabled = false,
  durationSeconds = 0,
  error,
  isLoading = false,
  isPlaying,
  menuActions = [],
  onAction,
  onPlayPause,
  onSeek,
  onSkip,
  skipSeconds = 15,
}: AudioPlayerProps) {
  const controlsDisabled = disabled || Boolean(error) || isLoading;
  const progressMax = Math.max(durationSeconds, 1);
  const progressValue = Math.min(Math.max(currentTimeSeconds, 0), progressMax);
  // Counting down rather than showing the total keeps the readout tied to the
  // playhead: the number and the bar always describe the same position.
  const remainingSeconds = Math.max(durationSeconds - progressValue, 0);

  function handlePlayPause() {
    onAction?.(isPlaying ? "pause" : "play");
    onPlayPause?.();
  }

  function handleSeek(event: ChangeEvent<HTMLInputElement>) {
    const nextTime = Number(event.target.value);
    onAction?.("seek");
    onSeek?.(nextTime);
  }

  function handleSkip(offsetSeconds: number) {
    onAction?.("skip");
    onSkip?.(offsetSeconds);
  }

  return (
    <section aria-label={ariaLabel} className={className}>
      {isLoading ? (
        <p aria-live="polite" className="sr-only" role="status">
          Loading audio
        </p>
      ) : null}
      {error ? (
        <p
          aria-live="polite"
          className="mb-3 flex items-center gap-2 font-medium text-red-700 text-sm"
          role="alert"
        >
          <AlertCircle aria-hidden="true" className="size-4 shrink-0" />
          {error}
        </p>
      ) : null}

      <div className="relative flex items-center gap-3 text-new-beginnings-base sm:gap-4">
        <AudioPlayerSkipButton
          direction="back"
          disabled={controlsDisabled}
          onSkip={handleSkip}
          skipSeconds={skipSeconds}
        />

        <AudioPlayerProgress
          disabled={controlsDisabled}
          durationSeconds={durationSeconds}
          onSeek={handleSeek}
          progressMax={progressMax}
          progressValue={progressValue}
        />

        <AudioPlayerSkipButton
          direction="forward"
          disabled={controlsDisabled}
          onSkip={handleSkip}
          skipSeconds={skipSeconds}
        />

        <AudioPlayerRemainingTime
          durationSeconds={durationSeconds}
          remainingSeconds={remainingSeconds}
        />

        {menuActions.length === 0 ? null : (
          <AudioPlayerMenu
            disabled={controlsDisabled}
            menuActions={menuActions}
          />
        )}

        <AudioPlayerPlayButton
          disabled={controlsDisabled}
          isLoading={isLoading}
          isPlaying={isPlaying}
          onPlayPause={handlePlayPause}
        />
      </div>
    </section>
  );
}

export { AudioPlayer };
